import React, { useContext, useEffect, useState } from 'react'
import Table, { ColumnProps } from '../../Common/Table'
import UserContext, { Roles } from '../../../UserContext'
import get from 'lodash/get'
import { ApiFetch } from '../../Common/Api'
import { displayDateTime, displayPhone } from '../../../utilities'
import type { Pageable, ServiceRequestService, ServiceResponse } from '../../../models'
import type { Sortable, PageFilters } from '../../../models/interfaces'

type ViewResponsesProps = { service: ServiceRequestService }

export const getResponseLabel = (record: ServiceResponse): string => {
    if (record.decision === 'Approved')
        return 'Accepted'
    // NoResponse is included because an SMS decline temporarily lands here before the backend
    // finalises the record as NoInterest. Grouping it with Declined prevents a "Pending" flash.
    if (record.status === 'NoInterest' || record.status === 'NoResponse' || record.decision === 'Rejected')
        return 'Declined'
    if (record.status === 'Interested')
        return 'Interested'
    return 'Pending'
}

/** Prefer lastModifiedOn so decline→accept (and other edits) show the latest response time. */
export const getResponseDate = (record: ServiceResponse): string | Date | undefined =>
    record.lastModifiedOn ?? record.createdOn

const ViewResponses: React.FC<ViewResponsesProps> = ({ service }) => {
    const [responses, setResponses] = useState<ServiceResponse[]>([])
    const [totalRows, setTotalRows] = useState(0)
    const [loading, setLoading] = useState(true)
    const [filterValues, setFilterValues] = useState<Sortable & PageFilters>({ rowIndex: 0, pageSize: 10, sort: [['createdOn', 'desc']] })
    const { hasAnyRole } = useContext(UserContext)

    useEffect(() => {
        setLoading(true)
        ApiFetch<Pageable<ServiceResponse>>('ServiceRequestServiceResponse', { serviceRequestServiceID: service.serviceRequestServiceID, ...filterValues })
            .then(({ results, totalRows }) => {
                setResponses(results)
                setTotalRows(totalRows)
            })
            // eslint-disable-next-line no-console
            .catch(console.log)
            .finally(() => setLoading(false))
    }, [service, filterValues])

    if (hasAnyRole(Roles.Provider)) return null

    const columns: ColumnProps<ServiceResponse> = [
        {
            title: 'Provider', dataIndex: 'user.name', sortable: true,
            render: (value, record) => <>
                {value}<br />
                <div className='contact_info'>
                    {get(record, 'user.contactName') && get(record, 'user.contactName') !== get(record, 'user.name') ? <>Contact: <span>{get(record, 'user.contactName')}</span><br /></> : null}
                    {get(record, 'user.contactPhone') && <><span>{displayPhone(get(record, 'user.contactPhone') as string)}</span><br /></>}
                    <span>{get(record, 'user.preferredEmailAddress') ? get(record, 'user.preferredEmailAddress') : get(record, 'user.emailAddress')}</span>
                </div>
            </>
        },
        { title: 'Provider Response', dataIndex: 'status', render: (_, record) => getResponseLabel(record), sortable: true },
        { title: 'Response Date', dataIndex: 'createdOn', render: (_, record) => displayDateTime(getResponseDate(record)), sortable: true }
    ]

    return (
        <>
            <h5><i className='far fa-files-medical'></i>Service Responses</h5>
            <br />
            {
                responses.length
                    ? <Table
                        columns={columns}
                        data={responses}
                        loading={loading}
                        totalRows={totalRows}
                        filters={filterValues}
                        setFilters={setFilterValues}
                        disableMultiColumnSort
                    />
                    : <p>No Responses Yet</p>
            }
        </>
    )
}

export default ViewResponses
